/**
 * COORDINATE SYSTEMS IN THIS FILE
 * ─────────────────────────────────────────────────────────────────────────────
 * Two coordinate systems are used throughout the Radar feature:
 *
 * 1. CELL / GRID SPACE  (used by the CSS grid UI and draw interactions)
 *    - (row, col) integers, both in range [0, 15] for a 16x16 grid.
 *    - row 0 is the TOP row (closest to the sensor), row 15 is the bottom.
 *    - col 0 is the LEFT column, col 15 is the right.
 *    - Cell key strings are formatted as "row,col".
 *
 * 2. ZONE / DEVICE SPACE  (used for API payloads and storage — do not change)
 *    - Rects stored as [x1, y1, x2, y2] tuples with a 180° rotation applied.
 *    - The device expects this exact format; never alter the storage format.
 *
 * BRIDGES between the two systems:
 *    - cellRectToZonePosition([r1,c1,r2,c2])  →  zone-space rect
 *      (cell drag → zone rect to send to device / add/subtract area)
 *    - zoneRectToCellKeys(zoneRect)           →  "row,col" string[]
 *      (zone rect from device → cell keys for CSS grid rendering)
 *    - invertZoneCoords(zoneRect)             →  visual bounds [vx1,vy1,vx2,vy2]
 *      (internal helper shared by both above)
 * ─────────────────────────────────────────────────────────────────────────────
 */

// Constants for grid and zone configurations
export const ZONE_LIMIT = 5; // Maximum zones the device UI allows
export const ROW_LENGTH = 16; // Number of columns per grid row
export const DEFAULT_ARR_LENGTH = 256; // Total grid cells (16 * 16 = 256)
export const WIDTH_TILE = 16; // Tile size changed from 20 to 16

/** Device/config blobs use `enable`; UI `Zone` uses `enabled`. Keeps toggles in sync with firmware. */
export function zoneEnabledFromDevice(zone: { enabled?: boolean; enable?: boolean }): boolean {
	if (typeof zone.enabled === 'boolean') return zone.enabled;
	return zone.enable !== false;
}

// Updated default room: now inverted on y-axis.
export const DEFAULT_ROOM = [[-(ROW_LENGTH / 2), WIDTH_TILE, ROW_LENGTH / 2, 0]];

/** Min/max detection heights must stay at least this far apart (m). */
export const DETECTION_HEIGHT_GAP_M = 0.1;
/** Absolute upper bound for max detection height (m). */
export const DETECTION_HEIGHT_MAX_M = 5;

export function minDetectionHeightInputMaxBound(parsedMaxM: number): number {
	if (!Number.isFinite(parsedMaxM)) {
		return DETECTION_HEIGHT_MAX_M - DETECTION_HEIGHT_GAP_M;
	}
	return Math.max(
		0,
		Math.min(DETECTION_HEIGHT_MAX_M - DETECTION_HEIGHT_GAP_M, parsedMaxM - DETECTION_HEIGHT_GAP_M),
	);
}

export function maxDetectionHeightInputMinBound(parsedMinM: number): number {
	if (!Number.isFinite(parsedMinM)) {
		return DETECTION_HEIGHT_GAP_M;
	}
	return Math.min(
		DETECTION_HEIGHT_MAX_M,
		Math.max(DETECTION_HEIGHT_GAP_M, parsedMinM + DETECTION_HEIGHT_GAP_M),
	);
}

// Validation ranges for zone configuration inputs (thresholds only — heights use validateZone*Height)
export const ZONE_VALIDATION_RANGES = {
	PRESENCE_THRESHOLD: { min: 0, max: 3600, step: 1 },
	ABSENCE_THRESHOLD: { min: 0, max: 3600, step: 1 },
} as const;

/** Issue codes from {@link validateZoneDetectionHeights}; localize with {@link formatHeightIssue} / {@link formatZoneDetectionHeightErrors}. */
export type HeightValidationIssue = {
	key: string;
	values?: Record<string, string | number>;
};

export type ZoneDetectionHeightValidation = {
	minIssue: HeightValidationIssue | null;
	maxIssue: HeightValidationIssue | null;
};

const GAP_DISPLAY = String(DETECTION_HEIGHT_GAP_M);

/**
 * Enforces: min ∈ [0, max−0.1], max ∈ [min+0.1, 5], valid numbers.
 * Gap violations use a single friendly message on both fields.
 */
export function validateZoneDetectionHeights(
	minValue: string,
	maxValue: string,
): ZoneDetectionHeightValidation {
	let minIssue: HeightValidationIssue | null = null;
	let maxIssue: HeightValidationIssue | null = null;

	const min = parseFloat(String(minValue).trim());
	const max = parseFloat(String(maxValue).trim());

	if (Number.isNaN(min)) {
		minIssue = { key: 'presence_height_error_min_invalid' };
	}
	if (Number.isNaN(max)) {
		maxIssue = { key: 'presence_height_error_max_invalid' };
	}

	if (minIssue || maxIssue) {
		return { minIssue, maxIssue };
	}

	if (min < -1e-6) {
		minIssue = { key: 'presence_height_error_min_negative' };
	}

	if (max > DETECTION_HEIGHT_MAX_M + 1e-6) {
		maxIssue = {
			key: 'presence_height_error_max_over_limit',
			values: { max_m: DETECTION_HEIGHT_MAX_M },
		};
	}

	if (minIssue || maxIssue) {
		return { minIssue, maxIssue };
	}

	if (min > max + 1e-6) {
		const orderIssue: HeightValidationIssue = { key: 'presence_height_error_min_above_max' };
		minIssue = orderIssue;
		maxIssue = orderIssue;
		return { minIssue, maxIssue };
	}

	/** Need at least DETECTION_HEIGHT_GAP_M between min and max; max must be ≤ 5 m (already checked). */
	if (max < min + DETECTION_HEIGHT_GAP_M - 1e-6) {
		const gapIssue: HeightValidationIssue = {
			key: 'presence_height_error_gap',
			values: { gap: GAP_DISPLAY },
		};
		minIssue = gapIssue;
		maxIssue = gapIssue;
	}

	return { minIssue, maxIssue };
}

export type DetectionHeightFormatFn = (
	key: string,
	opts: { default: string; values?: Record<string, string | number> },
) => string;

/** Pass `get(format)` from svelte-i18n. Defaults mirror `presence_height_validation.merge.json`. */
export function formatHeightIssue(
	issue: HeightValidationIssue | null,
	f: DetectionHeightFormatFn,
): string {
	if (!issue) return '';
	const v = issue.values;
	switch (issue.key) {
		case 'presence_height_error_min_invalid':
			return f('presence_height_error_min_invalid', {
				default: 'Enter a valid number for min detection height.',
			});
		case 'presence_height_error_max_invalid':
			return f('presence_height_error_max_invalid', {
				default: 'Enter a valid number for max detection height.',
			});
		case 'presence_height_error_min_negative':
			return f('presence_height_error_min_negative', {
				default: 'Min detection height can’t be below 0 m.',
			});
		case 'presence_height_error_max_over_limit':
			return f('presence_height_error_max_over_limit', {
				default: 'Max detection height can’t be above {max_m} m.',
				values: v,
			});
		case 'presence_height_error_min_above_max':
			return f('presence_height_error_min_above_max', {
				default: 'Min detection height can’t be higher than max detection height.',
			});
		case 'presence_height_error_gap':
			return f('presence_height_error_gap', {
				default: 'Min and max detection height must be at least {gap} m apart.',
				values: v,
			});
		default:
			return f(issue.key, { default: issue.key, values: v });
	}
}

export function formatZoneDetectionHeightErrors(
	validation: ZoneDetectionHeightValidation,
	f: DetectionHeightFormatFn,
): { minError: string; maxError: string } {
	return {
		minError: formatHeightIssue(validation.minIssue, f),
		maxError: formatHeightIssue(validation.maxIssue, f),
	};
}

/** Avoids duplicate text in toasts when min and max share one issue. */
export function detectionHeightToastTitle(minError: string, maxError: string): string {
	if (minError && maxError && minError === maxError) return minError;
	return [minError, maxError].filter(Boolean).join(' · ');
}

// Zone color palette (ordered by zone sequence)
export const ZONE_COLOR_PALETTE = [
	[255, 255, 255], // #FFFFFF - Main/Zone1 (white)
	[119, 92, 255], // #775CFF - Zone2 (purple)
	[46, 174, 248], // #2EAEF8 - Zone3 (blue)
	[65, 209, 180], // #41D1B4 - Zone4 (green/teal)
	[249, 202, 31], // #F9CA1F - Zone5 (yellow)
	[246, 123, 28], // #F67B1C - Zone6 (orange)
	[248, 55, 27], // #F8371B - Zone7 (red)
	[249, 54, 138], // #F9368A - Zone8 (pink)
	[0, 84, 255], // #0054FF - Zone9 (dark blue)
	[13, 131, 76], // #0D834C - Zone10 (dark green)
] as const;

/**
 * Validates zone configuration input values
 * @param value - The value to validate (as string)
 * @param type - The type of input to validate
 * @returns Object with isValid boolean and optional error message
 */
export function validateZoneInput(
	value: string,
	type: keyof typeof ZONE_VALIDATION_RANGES,
): { isValid: boolean; error?: string } {
	const numValue = parseFloat(value);

	if (isNaN(numValue)) {
		return { isValid: false, error: 'Please enter a valid number' };
	}

	const range = ZONE_VALIDATION_RANGES[type];

	if (numValue < range.min) {
		return { isValid: false, error: `Value must be at least ${range.min}` };
	}

	if (numValue > range.max) {
		return { isValid: false, error: `Value must be at most ${range.max}` };
	}

	return { isValid: true };
}

// Map to store colors for radar points keyed by their id
const radarPointColors = new Map<number | string, string>();

// -------------------------
// Color Management
// -------------------------

function generateZoneColor(index: number): string {
	const colorArray = ZONE_COLOR_PALETTE[index % ZONE_COLOR_PALETTE.length];
	// White would be invisible on a light background; use gray instead.
	if (colorArray[0] === 255 && colorArray[1] === 255 && colorArray[2] === 255) {
		return rgbToCss([200, 200, 200]);
	}
	return rgbToCss([...colorArray]);
}

/**
 * Gets or assigns a consistent color for a radar point/object.
 * Each new ID gets the next color in the palette in sequence.
 */
export function getOrAssignColor(id: number | string): string {
	if (!radarPointColors.has(id)) {
		radarPointColors.set(id, generateZoneColor(radarPointColors.size));
	}
	return radarPointColors.get(id)!;
}

/** Sets a specific color for a radar point/object. */
export function setPointColor(id: number | string, color: string): void {
	radarPointColors.set(id, color);
}

/** Clears all stored radar-point colors (e.g. on device reset). */
export function clearPointColors(): void {
	radarPointColors.clear();
}

// -------------------------
// Coordinate Conversion Helpers
// -------------------------

/**
 * Converts radar device coordinates (metres) directly to canvas pixel coordinates.
 * Mirrors the X axis because the radar X axis is inverted relative to screen coordinates.
 */
export function radarToCanvasDirect(
	radarX: number,
	radarY: number,
	canvasWidth: number,
	canvasHeight: number,
	radarRangeMeters: number = 8,
): { x: number; y: number } {
	const pixelsPerMeter = Math.min(canvasWidth, canvasHeight) / radarRangeMeters;
	const canvasX = canvasWidth / 2 + -radarX * pixelsPerMeter;
	const canvasY = radarY * pixelsPerMeter;
	return {
		x: Math.max(0, Math.min(canvasWidth, canvasX)),
		y: Math.max(0, Math.min(canvasHeight, canvasY)),
	};
}

// Converts a cell rectangle [row1, col1, row2, col2] to zone coordinates with y inversion and 180° rotation.
export function cellRectToZonePosition(
	rect: [number, number, number, number],
): [number, number, number, number] {
	const GRID_ROWS = DEFAULT_ARR_LENGTH / ROW_LENGTH;
	const roomX1 = DEFAULT_ROOM[0][0];
	const [row1, col1, row2, col2] = rect;
	// Unrotated zone coordinates.
	const zoneX1 = Math.min(col1, col2) + roomX1;
	const zoneX2 = Math.max(col1, col2) + roomX1;
	const zoneY1 = GRID_ROWS - Math.max(row1, row2);
	const zoneY2 = GRID_ROWS - Math.min(row1, row2);
	// Apply 180 degree rotation about (0, WIDTH_TILE/2) i.e. (0, 8) when WIDTH_TILE = 16.
	// Rotation: (x, y) -> (-x, 16 - y)
	const rx1 = -zoneX2;
	const ry1 = WIDTH_TILE - zoneY2;
	const rx2 = -zoneX1;
	const ry2 = WIDTH_TILE - zoneY1;
	return [Math.min(rx1, rx2), Math.min(ry1, ry2), Math.max(rx1, rx2), Math.max(ry1, ry2)];
}

// New helper: converts model (inverted) zone coordinates into visual coordinates.
export function invertZoneCoords(
	zone: [number, number, number, number],
): [number, number, number, number] {
	// Using transformation: (x, y) -> (-x, WIDTH_TILE - y)
	const [x1, y1, x2, y2] = zone;
	const vx1 = Math.min(-x1, -x2);
	const vx2 = Math.max(-x1, -x2);
	const vy1 = Math.min(WIDTH_TILE - y1, WIDTH_TILE - y2);
	const vy2 = Math.max(WIDTH_TILE - y1, WIDTH_TILE - y2);
	return [vx1, vy1, vx2, vy2];
}

/** Normalizes a zone-space rectangle to [min, min, max, max] (device storage order–invariant). */
export function normalizeZoneRect(
	rect: [number, number, number, number],
): [number, number, number, number] {
	const [a, b, c, d] = rect;
	return [Math.min(a, c), Math.min(b, d), Math.max(a, c), Math.max(b, d)];
}

/**
 * Intersection of two rects in device zone space, or null if disjoint or degenerate.
 * Used when clipping strokes to room boundaries so raw device tuples cannot zero-out the clip.
 */
export function intersectZoneRects(
	a: [number, number, number, number],
	b: [number, number, number, number],
): [number, number, number, number] | null {
	const [ax1, ay1, ax2, ay2] = normalizeZoneRect(a);
	const [bx1, by1, bx2, by2] = normalizeZoneRect(b);
	const ix1 = Math.max(ax1, bx1);
	const iy1 = Math.max(ay1, by1);
	const ix2 = Math.min(ax2, bx2);
	const iy2 = Math.min(ay2, by2);
	if (ix1 >= ix2 || iy1 >= iy2) return null;
	return [ix1, iy1, ix2, iy2];
}

// Helper to normalize a rectangle to [min, min, max, max] form.
function normalizeRect(rect: [number, number, number, number]): [number, number, number, number] {
	return normalizeZoneRect(rect);
}

// Updated subtractRect to operate on normalized rectangles.
function subtractRect(
	base: [number, number, number, number],
	sub: [number, number, number, number],
): [number, number, number, number][] {
	const [r1, c1, r2, c2] = normalizeRect(base);
	const [R1, C1, R2, C2] = normalizeRect(sub);
	const or1 = Math.max(r1, R1);
	const oc1 = Math.max(c1, C1);
	const or2 = Math.min(r2, R2);
	const oc2 = Math.min(c2, C2);
	if (or1 >= or2 || oc1 >= oc2) {
		// No overlap.
		return [base];
	}
	const result: [number, number, number, number][] = [];
	if (r1 < or1) {
		result.push([r1, c1, or1, c2]);
	}
	if (or2 < r2) {
		result.push([or2, c1, r2, c2]);
	}
	if (c1 < oc1) {
		result.push([or1, c1, or2, oc1]);
	}
	if (oc2 < c2) {
		result.push([or1, oc2, or2, c2]);
	}
	return result;
}

// Updated subtractArea to normalize resulting areas before filtering.
function subtractArea(
	existing: [number, number, number, number][],
	eraseRect: [number, number, number, number],
): [number, number, number, number][] {
	let result: [number, number, number, number][] = [];
	existing.forEach((area) => {
		const subtracted = subtractRect(area, eraseRect);
		result.push(...subtracted);
	});
	return result.filter((rect) => {
		const [a, b, c, d] = normalizeRect(rect);
		return c - a > 0 && d - b > 0;
	});
}

// Converts a color array to a CSS rgb string.
export function rgbToCss(color: number[]): string {
	return `rgb(${color[0]}, ${color[1]}, ${color[2]})`;
}

// Adds a new rectangle to an existing set of areas while skipping overlaps.
function addArea(
	existing: [number, number, number, number][],
	newRect: [number, number, number, number],
): [number, number, number, number][] {
	const result: [number, number, number, number][] = [];
	let remainingRect = [newRect]; // Start with the full new rectangle
	existing.forEach((area) => {
		const updatedRemaining: [number, number, number, number][] = [];
		remainingRect.forEach((rect) => {
			updatedRemaining.push(...subtractRect(rect, area));
		});
		remainingRect = updatedRemaining;
		result.push(area); // keep the existing area
	});
	// Add only the non-overlapping parts of the new rectangle.
	result.push(...remainingRect);
	return result;
}

// -------------------------
// Area Optimization Functions
// -------------------------

// Checks if two rectangles are adjacent (touching) on any side
function areRectanglesAdjacent(
	rect1: [number, number, number, number],
	rect2: [number, number, number, number],
): boolean {
	const [x1, y1, x2, y2] = normalizeRect(rect1);
	const [X1, Y1, X2, Y2] = normalizeRect(rect2);

	// Check if rectangles are adjacent horizontally
	const horizontallyAdjacent =
		// rect1 is to the left of rect2
		(x2 === X1 && y1 < Y2 && y2 > Y1) ||
		// rect1 is to the right of rect2
		(x1 === X2 && y1 < Y2 && y2 > Y1);

	// Check if rectangles are adjacent vertically
	const verticallyAdjacent =
		// rect1 is above rect2
		(y2 === Y1 && x1 < X2 && x2 > X1) ||
		// rect1 is below rect2
		(y1 === Y2 && x1 < X2 && x2 > X1);

	return horizontallyAdjacent || verticallyAdjacent;
}

// Attempts to merge two adjacent rectangles into one larger rectangle
function mergeAdjacentRectangles(
	rect1: [number, number, number, number],
	rect2: [number, number, number, number],
): [number, number, number, number] | null {
	const [x1, y1, x2, y2] = normalizeRect(rect1);
	const [X1, Y1, X2, Y2] = normalizeRect(rect2);

	// Check if rectangles can be merged horizontally
	if (y1 === Y1 && y2 === Y2) {
		// Same height, check if adjacent horizontally
		if (x2 === X1) {
			// rect1 is to the left of rect2
			return [x1, y1, X2, y2];
		} else if (X2 === x1) {
			// rect2 is to the left of rect1
			return [X1, y1, x2, y2];
		}
	}

	// Check if rectangles can be merged vertically
	if (x1 === X1 && x2 === X2) {
		// Same width, check if adjacent vertically
		if (y2 === Y1) {
			// rect1 is above rect2
			return [x1, y1, x2, Y2];
		} else if (Y2 === y1) {
			// rect2 is above rect1
			return [x1, Y1, x2, y2];
		}
	}

	return null; // Cannot merge
}

// Optimizes an array of rectangles by merging adjacent ones
export function optimizeRectangleArray(
	rectangles: [number, number, number, number][],
): [number, number, number, number][] {
	if (rectangles.length <= 1) {
		return [...rectangles];
	}

	let optimized = [...rectangles];
	let foundMerge = true;

	// Keep trying to merge until no more merges are possible
	while (foundMerge) {
		foundMerge = false;
		const newOptimized: [number, number, number, number][] = [];
		const used = new Set<number>();

		for (let i = 0; i < optimized.length; i++) {
			if (used.has(i)) continue;

			let currentRect = optimized[i];
			let merged = false;

			// Try to merge with any subsequent rectangle
			for (let j = i + 1; j < optimized.length; j++) {
				if (used.has(j)) continue;

				if (areRectanglesAdjacent(currentRect, optimized[j])) {
					const mergedRect = mergeAdjacentRectangles(currentRect, optimized[j]);
					if (mergedRect) {
						currentRect = mergedRect;
						used.add(j);
						merged = true;
						foundMerge = true;
					}
				}
			}

			newOptimized.push(currentRect);
			used.add(i);
		}

		optimized = newOptimized;
	}

	return optimized;
}

// Enhanced addArea function with optimization
export function addAreaOptimized(
	existing: [number, number, number, number][],
	newRect: [number, number, number, number],
): [number, number, number, number][] {
	// First, add the area using the existing logic
	const result = addArea(existing, newRect);

	// Then optimize by merging adjacent rectangles
	return optimizeRectangleArray(result);
}

// Enhanced subtractArea function with optimization
export function subtractAreaOptimized(
	existing: [number, number, number, number][],
	eraseRect: [number, number, number, number],
): [number, number, number, number][] {
	// First, subtract using the existing logic
	const result = subtractArea(existing, eraseRect);

	// Then optimize by merging adjacent rectangles
	return optimizeRectangleArray(result);
}

// -------------------------
// CSS Grid Rendering Helper
// -------------------------

/**
 * Converts a single zone-space rect (device format) into an array of cell key
 * strings ("row,col") that the CSS-grid renderer should highlight.
 *
 * Coordinate mapping (mirrors the pixel math used by the old Canvas renderer):
 *   col  = round(vx1 - roomX1)  ..  round(vx2 - roomX1)  - 1
 *   row  = round(GRID_ROWS - vy2)  ..  round(GRID_ROWS - vy1)  - 1
 *
 * where vx, vy come from invertZoneCoords() (visual / un-rotated coords).
 *
 * Only cells that fall within the 16×16 grid (row/col 0..15) are returned.
 */
export function zoneRectToCellKeys(zoneRect: [number, number, number, number]): string[] {
	const GRID_ROWS = ROW_LENGTH; // 16
	const roomX1 = DEFAULT_ROOM[0][0]; // −8

	const [vx1, vy1, vx2, vy2] = invertZoneCoords(zoneRect);

	const colStart = Math.round(vx1 - roomX1);
	const colEnd = Math.round(vx2 - roomX1);
	const rowStart = Math.round(GRID_ROWS - vy2);
	const rowEnd = Math.round(GRID_ROWS - vy1);

	const keys: string[] = [];
	for (let row = rowStart; row < rowEnd; row++) {
		for (let col = colStart; col < colEnd; col++) {
			if (row >= 0 && row < GRID_ROWS && col >= 0 && col < ROW_LENGTH) {
				keys.push(`${row},${col}`);
			}
		}
	}
	return keys;
}

/**
 * Expands an array of zone-space rects into a deduplicated Set of cell keys.
 * Convenience wrapper around zoneRectToCellKeys for rendering a full zone.area.
 */
export function zoneAreaToCellKeys(area: [number, number, number, number][]): Set<string> {
	const keys = new Set<string>();
	for (const rect of area) {
		for (const key of zoneRectToCellKeys(rect)) {
			keys.add(key);
		}
	}
	return keys;
}
